/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { IncomingHttpHeaders } from 'http';
import environmentConfig from '@/config/environment';
import type {
  ForteData,
  ForteFlagValue,
  ForteFlags,
  ForteFlagName,
  BooleanForteFlagName,
} from '../interface';
import { FORTE_FLAGS_DISABLED_TRUE_DEFAULT } from '../consntants';
import type { Locale } from '@/types/i18n';
import { getLocaleFromCache, useLocaleFromCache } from '@/hooks/useLocaleQuery';

const FT_HEADER_PREFIX = 'ft-';

const kebabToCamelCase = (value: string) =>
  value.replace(/-([a-z0-9])/g, (_, char: string) => char.toUpperCase());

const parseForteHeaderValue = (value: string): ForteFlagValue => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  const asNumber = Number(value);
  if (!isNaN(asNumber) && value.trim() !== '') {
    return asNumber;
  }
  return value;
};

const buildForteDataFromHeaders = (headers: IncomingHttpHeaders): ForteData => {
  const flags: Record<string, ForteFlagValue> = {};

  for (const [headerName, headerValue] of Object.entries(headers)) {
    if (!headerName.startsWith(FT_HEADER_PREFIX) || headerValue === undefined) {
      continue;
    }

    const flagKey = kebabToCamelCase(headerName.slice(FT_HEADER_PREFIX.length));
    const rawValue = Array.isArray(headerValue) ? headerValue[0] : headerValue;
    flags[flagKey] = parseForteHeaderValue(rawValue);
  }

  return {
    flags,
  };
};

const getForteDecisionFromCache = (queryClient: QueryClient) => {
  return queryClient.getQueryData<ForteData>(getForteDecisionKey());
};

const selectValue = <F extends ForteFlagName>(
  data: ForteData | undefined,
  flag: F,
): ForteFlags[F] | undefined => {
  if (!data || !(flag in data.flags)) {
    return undefined;
  }

  return data.flags[flag as string] as ForteFlags[F];
};

/**
 * Get the value of a Forte flag.
 *
 * Important: Always use `useForteDecisionFlag` inside React components.
 * This prevents the query data being garbage collected,
 * and allows local cache updates to be reflected in the UI.
 */
const getForteDecisionFlag = <F extends ForteFlagName>(
  queryClient: QueryClient,
  flag: F,
): ForteFlags[F] | undefined => {
  const forteDecisionData = getForteDecisionFromCache(queryClient);

  return selectValue(forteDecisionData, flag);
};

const initialData: ForteData = {
  flags: {},
};

/**
 * Get the value of a Forte flag.
 */
export const useForteDecisionFlag = <F extends ForteFlagName>(
  flag: F,
): ForteFlags[F] | undefined => {
  const select = useCallback((data: ForteData) => selectValue(data, flag), [flag]);
  const { data: decisionFlag } = useQuery({
    queryKey: getForteDecisionKey(),
    queryFn: () => {
      throw new Error('Query prefetch failed');
    },
    select,
    initialData,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return decisionFlag as ForteFlags[F] | undefined;
};

const applyTrueDefault = (flagValue: boolean | undefined): boolean => flagValue ?? true;

const shouldApplyTrueDefault = (flag: BooleanForteFlagName, locale: Locale): boolean => {
  const disabledLocales = FORTE_FLAGS_DISABLED_TRUE_DEFAULT[flag] || [];
  return !disabledLocales.includes(locale);
};

/**
 * True-default variant of the Forte flag accessors: returns `true` when the flag is absent,
 * `false` when explicitly disabled, and `true` when explicitly enabled.
 *
 * In production, returns the actual flag value without applying true default.
 * Use these instead of the base flag accessors for features that should be on by default (non-prod).
 */
export const useForteDecisionFlagWithTrueDefault = <F extends BooleanForteFlagName>(
  flag: F,
): boolean => {
  const flagValue = useForteDecisionFlag(flag) as boolean | undefined;
  const locale = useLocaleFromCache();
  const isNonProd = environmentConfig.TARGET_ENV !== 'production';
  const shouldApplyDefault = isNonProd && shouldApplyTrueDefault(flag, locale!);
  return shouldApplyDefault ? applyTrueDefault(flagValue) : (flagValue ?? false);
};

const getForteDecisionKey = () => ['forte'] as const;

const prefetchForteDecision = ({
  queryClient,
  headers,
}: {
  queryClient: QueryClient;
  headers: IncomingHttpHeaders;
}) => {
  queryClient.setQueryData(getForteDecisionKey(), buildForteDataFromHeaders(headers));
};

const getForteDecisionFlagWithTrueDefault = <F extends BooleanForteFlagName>(
  queryClient: QueryClient,
  flag: F,
): boolean => {
  const flagValue = getForteDecisionFlag(queryClient, flag) as boolean | undefined;
  const locale = getLocaleFromCache(queryClient);
  const isNonProd = environmentConfig.TARGET_ENV !== 'production';
  const shouldApplyDefault = isNonProd && shouldApplyTrueDefault(flag, locale!);
  return shouldApplyDefault ? applyTrueDefault(flagValue) : (flagValue ?? false);
};

export {
  getForteDecisionKey,
  getForteDecisionFromCache,
  prefetchForteDecision,
  getForteDecisionFlag,
  getForteDecisionFlagWithTrueDefault,
};
